import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { assetPath } from '@/utils/assetPath'
import { useState, useCallback } from 'react'
import { ArrowLeft } from 'lucide-react'
import { GlossyButton } from '@/components/shared/atoms/GlossyButton'
import { GlossyInput } from '@/components/auth/GlossyInput'
import { FadeInUp } from '@/components/shared/atoms/FadeInUp'
import { useSendPasswordResetEmail } from '@/hooks/api/useAuth'

export const Route = createFileRoute('/(onboarding)/(auth)/forgot-password_')({
    component: ForgotPasswordRoute,
})

function ForgotPasswordRoute() {
    const [email, setEmail] = useState('')
    const navigate = useNavigate()
    const [apiError, setApiError] = useState<string | null>(null)
    const { mutate: sendResetEmail, isPending: isSending } =
        useSendPasswordResetEmail()

    const handleSignUpButton = useCallback(() => {
        navigate({ to: '/register' })
    }, [navigate])

    const handleBackButton = useCallback(() => {
        navigate({ to: '/login' })
    }, [navigate])

    const handleSendResetButton = useCallback(() => {
        setApiError(null)
        sendResetEmail(
            { email },
            {
                onSuccess: () =>
                    navigate({
                        to: '/forgot-password/sent',
                        search: { email },
                    }),
                onError: (err: Error) => setApiError(err.message),
            }
        )
    }, [email, navigate, sendResetEmail])

    return (
        <section className="relative z-10 flex min-h-dvh flex-col pt-safe">
            {/* Logo & Sign Up Button - OUTSIDE FadeInUp */}
            <div className="flex justify-between px-2 pt-3 md:justify-center">
                <img
                    alt="logo-red"
                    src={assetPath('logo/logo-red.svg')}
                    className="h-[34px] w-[155px] md:mt-12 md:h-[100px] md:w-[440px]"
                />
                <GlossyButton
                    variant="ghost"
                    className="rounded-xl bg-white/25 px-4 py-2.5 backdrop-blur md:fixed md:right-2.5"
                    onClick={handleSignUpButton}
                    disabled={isSending}
                >
                    Sign up
                </GlossyButton>
            </div>

            {/* Heading - Separate FadeInUp */}
            <FadeInUp delay={0.3}>
                <div className="px-4 pt-2 pb-6 md:pt-[35vh] md:text-center">
                    <h1 className="font-sfpro text-28 leading-8 font-semibold text-white">
                        Forgot password
                    </h1>
                    <p className="text-13 mt-3 font-normal text-white">
                        Enter the email address associated with your account,
                        and we'll email you a link to reset your password.
                    </p>
                </div>
            </FadeInUp>

            {/* Main Content Container */}
            <div className="flex flex-1 flex-col px-2">
                <FadeInUp delay={0.5}>
                    <div className="space-y-1">
                        <GlossyInput
                            placeholder="Email address"
                            onChange={(e) => setEmail(e.target.value)}
                            glassEffect={{
                                opacity: 'bg-white/20',
                                blur: 'backdrop-blur-md',
                            }}
                        />
                    </div>
                    {apiError && (
                        <div className="mt-2 text-center text-sm text-red-400">
                            {apiError}
                        </div>
                    )}
                </FadeInUp>

                <FadeInUp delay={0.7} className="mt-auto mb-6">
                    <div className="flex items-center gap-2 pt-6">
                        <GlossyButton
                            variant="ghost"
                            className="flex-none rounded-xl bg-white/20 px-4 py-[14px] text-sm font-medium text-white backdrop-blur-md"
                            onClick={handleBackButton}
                        >
                            <ArrowLeft
                                strokeWidth={1}
                                className="mr-1 h-5 w-5"
                            />
                            Back
                        </GlossyButton>
                        <GlossyButton
                            className="w-full rounded-xl py-[13px] text-sm"
                            onClick={handleSendResetButton}
                            disabled={isSending}
                        >
                            {isSending ? 'Sending...' : 'Send Reset Link'}
                        </GlossyButton>
                    </div>
                </FadeInUp>
            </div>
        </section>
    )
}
